import React, { useState, useEffect } from 'react';

const DivisionCuenta = ({ 
  productosVenta, 
  totalVenta,
  onConfirmarDivision, 
  onCancelar,
  formatearNumero 
}) => {
  const [cuentas, setCuentas] = useState([
    { id: 1, nombre: 'Cuenta 1', productos: [], total: 0 }
  ]);
  const [productosAsignados, setProductosAsignados] = useState({});
  const [cuentaActiva, setCuentaActiva] = useState(1);
  const [nombreNuevaCuenta, setNombreNuevaCuenta] = useState('');

  // Calcular totales de cada cuenta
  useEffect(() => {
    const cuentasActualizadas = cuentas.map(cuenta => {
      const total = cuenta.productos.reduce((sum, prod) => 
        sum + (prod.precio * prod.cantidadAsignada), 0
      );
      return { ...cuenta, total };
    });
    setCuentas(cuentasActualizadas);
  }, [cuentas.map(c => c.productos).flat()]);

  const agregarCuenta = () => {
    if (!nombreNuevaCuenta.trim()) return;
    
    const nuevaCuenta = {
      id: Date.now(),
      nombre: nombreNuevaCuenta,
      productos: [],
      total: 0
    };
    setCuentas([...cuentas, nuevaCuenta]);
    setNombreNuevaCuenta('');
    setCuentaActiva(nuevaCuenta.id);
  };

  const eliminarCuenta = (cuentaId) => {
    if (cuentas.length <= 1) return;
    
    // Devolver productos al pool disponible
    const cuentaAEliminar = cuentas.find(c => c.id === cuentaId);
    cuentaAEliminar.productos.forEach(producto => {
      const disponible = productosAsignados[producto.id] || 0;
      setProductosAsignados(prev => ({
        ...prev,
        [producto.id]: disponible + producto.cantidadAsignada
      }));
    });

    setCuentas(cuentas.filter(c => c.id !== cuentaId));
    if (cuentaActiva === cuentaId) {
      setCuentaActiva(cuentas[0].id);
    }
  };

  const asignarProductoACuenta = (producto, cantidad) => {
    const cuentaSeleccionada = cuentas.find(c => c.id === cuentaActiva);
    const disponible = productosAsignados[producto.id] || producto.cantidadVenta;
    
    if (cantidad > disponible) {
      alert(`Solo quedan ${disponible} unidades disponibles de ${producto.nombre}`);
      return;
    }

    // Actualizar productos asignados
    setProductosAsignados(prev => ({
      ...prev,
      [producto.id]: disponible - cantidad
    }));

    // Agregar a la cuenta activa
    setCuentas(prev => prev.map(cuenta => {
      if (cuenta.id === cuentaActiva) {
        const productoExistente = cuenta.productos.find(p => p.id === producto.id);
        if (productoExistente) {
          return {
            ...cuenta,
            productos: cuenta.productos.map(p =>
              p.id === producto.id 
                ? { ...p, cantidadAsignada: p.cantidadAsignada + cantidad }
                : p
            )
          };
        } else {
          return {
            ...cuenta,
            productos: [...cuenta.productos, {
              ...producto,
              cantidadAsignada: cantidad
            }]
          };
        }
      }
      return cuenta;
    }));
  };

  const quitarProductoDeCuenta = (cuentaId, productoId, cantidadAQuitar) => {
    setCuentas(prev => prev.map(cuenta => {
      if (cuenta.id === cuentaId) {
        return {
          ...cuenta,
          productos: cuenta.productos.reduce((acc, producto) => {
            if (producto.id === productoId) {
              const nuevaCantidad = producto.cantidadAsignada - cantidadAQuitar;
              if (nuevaCantidad > 0) {
                acc.push({ ...producto, cantidadAsignada: nuevaCantidad });
              }
            } else {
              acc.push(producto);
            }
            return acc;
          }, [])
        };
      }
      return cuenta;
    }));

    // Devolver al pool disponible
    setProductosAsignados(prev => ({
      ...prev,
      [productoId]: (prev[productoId] || 0) + cantidadAQuitar
    }));
  };

  const validarDivision = () => {
    const totalAsignado = cuentas.reduce((sum, cuenta) => sum + cuenta.total, 0);
    const todoAsignado = productosVenta.every(producto => {
      const disponible = productosAsignados[producto.id] ?? producto.cantidadVenta;
      return disponible === 0;
    });

    return { todoAsignado, totalAsignado, totalOriginal: totalVenta };
  };

  const confirmarDivision = () => {
    const { todoAsignado, totalAsignado, totalOriginal } = validarDivision();
    
    if (!todoAsignado) {
      alert('Debe asignar todos los productos a las cuentas antes de continuar');
      return;
    }

    if (Math.abs(totalAsignado - totalOriginal) > 0.01) {
      alert(`Error: El total de las cuentas (${formatearNumero(totalAsignado)}) no coincide con el total original (${formatearNumero(totalOriginal)})`);
      return;
    }

    onConfirmarDivision(cuentas.filter(cuenta => cuenta.productos.length > 0));
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95%',
        maxWidth: '1200px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0 }}>🍽️ División de Cuenta</h3>
          <button
            onClick={onCancelar}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            ✖ Cancelar
          </button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Panel izquierdo - Productos disponibles */}
          <div style={{
            width: '300px',
            borderRight: '1px solid #dee2e6',
            padding: '20px',
            overflowY: 'auto'
          }}>
            <h4>📦 Productos Disponibles</h4>
            <div style={{ fontSize: '14px', marginBottom: '15px', color: '#6c757d' }}>
              Total Original: ${formatearNumero(totalVenta)}
            </div>
            
            {productosVenta.map(producto => {
              const disponible = productosAsignados[producto.id] ?? producto.cantidadVenta;
              return (
                <div key={producto.id} style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '10px',
                  backgroundColor: disponible === 0 ? '#f8f9fa' : 'white'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                    {producto.nombre}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>
                    Precio: ${formatearNumero(producto.precio)}
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                    Disponible: {disponible} / {producto.cantidadVenta}
                  </div>
                  
                  {disponible > 0 && (
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      <input
                        type="number"
                        min="1"
                        max={disponible}
                        defaultValue="1"
                        id={`cantidad-${producto.id}`}
                        style={{
                          width: '60px',
                          padding: '4px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '12px'
                        }}
                      />
                      <button
                        onClick={() => {
                          const cantidad = parseInt(document.getElementById(`cantidad-${producto.id}`).value) || 1;
                          asignarProductoACuenta(producto, cantidad);
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        ➕ Asignar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Panel central - Gestión de cuentas */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Tabs de cuentas */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid #dee2e6',
              backgroundColor: '#f8f9fa',
              padding: '0 20px',
              overflowX: 'auto'
            }}>
              {cuentas.map(cuenta => (
                <div
                  key={cuenta.id}
                  onClick={() => setCuentaActiva(cuenta.id)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: cuentaActiva === cuenta.id ? '3px solid #007bff' : '3px solid transparent',
                    backgroundColor: cuentaActiva === cuenta.id ? 'white' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{cuenta.nombre}</span>
                  <span style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '2px 6px',
                    fontSize: '11px'
                  }}>
                    ${formatearNumero(cuenta.total)}
                  </span>
                  {cuentas.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        eliminarCuenta(cuenta.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ✖
                    </button>
                  )}
                </div>
              ))}
              
              {/* Agregar nueva cuenta */}
              <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Nombre cuenta"
                  value={nombreNuevaCuenta}
                  onChange={(e) => setNombreNuevaCuenta(e.target.value)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '12px',
                    width: '120px'
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && agregarCuenta()}
                />
                <button
                  onClick={agregarCuenta}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px'
                  }}
                >
                  + Cuenta
                </button>
              </div>
            </div>

            {/* Contenido de la cuenta activa */}
            <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
              {(() => {
                const cuenta = cuentas.find(c => c.id === cuentaActiva);
                if (!cuenta) return null;

                return (
                  <div>
                    <h4>{cuenta.nombre}</h4>
                    {cuenta.productos.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#6c757d', marginTop: '40px' }}>
                        No hay productos asignados a esta cuenta.<br/>
                        Selecciona productos del panel izquierdo.
                      </p>
                    ) : (
                      <div>
                        {cuenta.productos.map(producto => (
                          <div key={producto.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            marginBottom: '8px'
                          }}>
                            <div>
                              <div style={{ fontWeight: 'bold' }}>{producto.nombre}</div>
                              <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                ${formatearNumero(producto.precio)} x {producto.cantidadAsignada}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontWeight: 'bold' }}>
                                ${formatearNumero(producto.precio * producto.cantidadAsignada)}
                              </span>
                              <button
                                onClick={() => quitarProductoDeCuenta(cuenta.id, producto.id, 1)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                ➖
                              </button>
                            </div>
                          </div>
                        ))}
                        <div style={{
                          marginTop: '20px',
                          padding: '15px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          textAlign: 'right'
                        }}>
                          <h4 style={{ margin: 0 }}>
                            Total: ${formatearNumero(cuenta.total)}
                          </h4>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Footer con resumen y botones */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '14px' }}>
              <strong>Resumen:</strong> {cuentas.length} cuentas • 
              Total: ${formatearNumero(cuentas.reduce((sum, c) => sum + c.total, 0))}
            </div>
            {(() => {
              const { todoAsignado } = validarDivision();
              return (
                <div style={{ 
                  fontSize: '12px', 
                  color: todoAsignado ? '#28a745' : '#dc3545',
                  marginTop: '5px'
                }}>
                  {todoAsignado ? '✅ Todos los productos asignados' : '⚠️ Faltan productos por asignar'}
                </div>
              );
            })()}
          </div>
          
          <button
            onClick={confirmarDivision}
            style={{
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            ✅ Confirmar División
          </button>
        </div>
      </div>
    </div>
  );
};

export default DivisionCuenta;
